import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/AuthContext";
import { Loader2, ArrowLeft, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/auth`;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Minimum 8 caractères."); return; }
    if (password !== confirm) { setError("Les mots de passe ne correspondent pas."); return; }
    if (!token) { setError("Lien invalide."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/reset-password`, { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-brand-paper flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-10">
          <img src="/picto-instant.png" alt="Agence Instant" className="h-9 w-9 object-contain" />
          <div className="h-9 w-px bg-brand-line" />
          <div className="leading-none">
            <div className="font-display text-xl text-brand-ink">Instant Studio</div>
            <div className="eyebrow mt-1">by Agence Instant</div>
          </div>
        </div>

        <div className="rounded-lg border border-brand-line bg-white p-8 shadow-card">
          <h1 className="font-display text-3xl text-brand-ink">Nouveau mot de passe</h1>
          <p className="mt-2 text-sm text-brand-subtle">
            Choisissez un nouveau mot de passe (minimum 8 caractères).
          </p>

          {done ? (
            <div className="mt-8 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 inline-flex items-center gap-2" data-testid="reset-done-message">
              <Check className="h-4 w-4" />
              Mot de passe mis à jour. Redirection…
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="reset-form">
              <div className="space-y-2">
                <Label htmlFor="password">Nouveau mot de passe</Label>
                <Input
                  id="password" type="password" autoComplete="new-password" required minLength={8}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Confirmation</Label>
                <Input
                  id="confirm" type="password" autoComplete="new-password" required minLength={8}
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm-input"
                />
              </div>
              {error && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="reset-error">
                  {error}
                </div>
              )}
              <Button type="submit" disabled={loading} className="w-full" data-testid="reset-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mettre à jour"}
              </Button>
            </form>
          )}

          <div className="mt-6">
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-brand-subtle hover:text-brand-ink">
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} />
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
